/**
 * Customer-facing impersonation token plumbing — Phase 1 Day 8.
 *
 * When the admin tab opens `peopleprints.app/?_imp=<token>`, the customer
 * surface reads the token from the query param on first load, stores it in
 * sessionStorage (so the impersonator can navigate the surface without
 * losing it), and surfaces a persistent Coral banner per spec §P1.4 with
 * "End impersonation now →".
 *
 * The impersonation token is used in place of the normal Supabase access
 * token when making /api/* calls — `requireAuth` detects the `pp_imp_`
 * prefix and routes to the impersonation context-builder.
 *
 * Token is never persisted to localStorage (so closing the tab kills it
 * for the impersonator even if the JWT exp hasn't fired). sessionStorage
 * is per-tab in modern browsers; opening a new tab requires a fresh
 * impersonation session.
 */

const STORAGE_KEY = "ppr.customer.impersonation_token";
const META_KEY = "ppr.customer.impersonation_meta";

/**
 * Bootstrap from URL query (?_imp=...). Called once at app startup. Strips
 * the param from the URL after capture so the token doesn't sit in the
 * address bar / browser history.
 */
export function bootstrapImpersonationFromUrl() {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  const token = params.get("_imp");
  if (!token) return false;
  sessionStorage.setItem(STORAGE_KEY, token);
  // Decode the payload (no signature check — server validates on every API
  // call). We only need the metadata for banner display.
  const meta = peekClaims(token);
  if (meta) sessionStorage.setItem(META_KEY, JSON.stringify(meta));
  // Strip the query param.
  params.delete("_imp");
  const newQs = params.toString();
  const newUrl = window.location.pathname + (newQs ? `?${newQs}` : "") + window.location.hash;
  window.history.replaceState({}, "", newUrl);
  return true;
}

export function getImpersonationToken() {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(STORAGE_KEY) || null;
}

export function getImpersonationMeta() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(META_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearImpersonation() {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(META_KEY);
}

/**
 * Decode the JWT payload WITHOUT signature verification. Safe — we only
 * read it for display purposes (banner countdown, target name); every
 * server call re-verifies. Returns null on malformed input.
 */
function peekClaims(token) {
  try {
    const stripped = token.startsWith("pp_imp_") ? token.slice("pp_imp_".length) : token;
    const parts = stripped.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    // base64url → base64
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(payload.length / 4) * 4, "=");
    // atom: browser btoa/atob takes binary strings; payload is JSON.
    const json = typeof atob === "function" ? atob(b64) : Buffer.from(b64, "base64").toString("utf8");
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Public export of `peekClaims` — used by `supabase.js` to synthesize a
 * minimal session when a fresh-browser impersonation visit has no
 * pre-existing customer-side session to overlay onto. Per Phase 1.6
 * Bug 3 fix (2026-05-12).
 */
export { peekClaims as peekImpersonationClaims };

// Retained alias for tests written against the prior name. Functionally
// identical to `peekImpersonationClaims`.
export { peekClaims as _peekClaimsForTest };
